// frontend/src/screens/admin/AdminOrderAnalyticsScreen.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft,
  Banknote,
  BarChart3,
  CheckCircle2,
  CreditCard,
  Loader2,
  PackageCheck,
  RefreshCw,
  RotateCcw,
  Truck,
} from "lucide-react";
import { toast } from "react-toastify";

import {
  getAdminOrderAnalytics,
  resetAdminOrderError,
  resetAdminOrderMeta,
} from "../../../actions/adminOrderActions";

import Loader from "../../../components/Loader";
import Message from "../../../components/Message";
import Meta from "../../../components/Meta";
import { getSafeUserMessage } from "../../../utils/errorMessages";

import "./AdminOrderAnalyticsScreen.css";

const DAY_OPTIONS = [7, 30, 60, 90, 180, 365];
const LIMIT_OPTIONS = [25, 50, 100];

function safe(value = "") {
  return String(value || "").trim();
}

function humanize(value = "", fallback = "—") {
  const text = safe(value);

  if (!text || text === "unknown") return fallback;

  return text
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatMoney(value = 0, currency = "GBP") {
  const amount = Number(value || 0);

  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: safe(currency) || "GBP",
    }).format(Number.isFinite(amount) ? amount : 0);
  } catch {
    return `£${(Number.isFinite(amount) ? amount : 0).toFixed(2)}`;
  }
}

function formatDate(value = "") {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
  }).format(date);
}

function formatDateTime(value = "") {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function getOrderDate(order = {}) {
  return order.createdAt || order.placedAt || "";
}

function getPaymentStatus(order = {}) {
  return safe(order.payment?.status || "not_started").toLowerCase();
}

function getOrderStatus(order = {}) {
  return safe(order.status || "").toLowerCase();
}

function getFulfillmentStatus(order = {}) {
  return safe(order.fulfillment?.status || "").toLowerCase();
}

function orderValue(order = {}) {
  return Number(
    order.grandTotal ||
      order.payment?.amountCaptured ||
      order.payment?.amount ||
      0,
  );
}

function normalizeBreakdownRows(rows = []) {
  return Array.isArray(rows)
    ? rows
        .map((row) => ({
          key: safe(row.key || row._id || "unknown") || "unknown",
          label: humanize(row.key || row._id || "unknown"),
          count: Number(row.count || 0),
        }))
        .sort((a, b) => b.count - a.count)
    : [];
}

function StatCard({ icon: Icon, title, value, subtitle }) {
  return (
    <article className="admin-order-analytics__stat-card">
      <div className="admin-order-analytics__stat-icon">
        <Icon size={20} />
      </div>

      <div>
        <span>{title}</span>
        <strong>{value}</strong>
        <small>{subtitle}</small>
      </div>
    </article>
  );
}

function BarList({ title, rows = [], total = 0, empty = "No data yet." }) {
  const max = Math.max(...rows.map((row) => Number(row.count || 0)), 1);

  return (
    <article className="admin-order-analytics__panel">
      <header>
        <h2>{title}</h2>
      </header>

      {rows.length === 0 ? (
        <div className="admin-order-analytics__empty-inline">{empty}</div>
      ) : (
        <div className="admin-order-analytics__bars">
          {rows.map((row) => {
            const width = Math.max(6, (Number(row.count || 0) / max) * 100);
            const percent =
              total > 0
                ? Math.round((Number(row.count || 0) / total) * 100)
                : 0;

            return (
              <div className="admin-order-analytics__bar-row" key={row.key}>
                <div>
                  <strong>{row.label}</strong>

                  <span>
                    {row.count} {row.count === 1 ? "order" : "orders"}
                    {total > 0 ? ` · ${percent}%` : ""}
                  </span>
                </div>

                <div className="admin-order-analytics__bar-track">
                  <span style={{ width: `${width}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </article>
  );
}

export default function AdminOrderAnalyticsScreen() {
  const dispatch = useDispatch();

  const adminOrderState = useSelector(
    (state) => state.adminOrder || {},
    shallowEqual,
  );

  const { error = null, pending = {}, analytics = {} } = adminOrderState;

  const {
    summary = {},
    breakdowns = {},
    revenueByDay = [],
    recentOrders = [],
    range = null,
    generatedAt = "",
  } = analytics;

  const [days, setDays] = useState(90);
  const [limit, setLimit] = useState(50);
  const [didLoad, setDidLoad] = useState(false);

  const loading = Boolean(pending.analytics && !didLoad);
  const refreshing = Boolean(pending.analytics);

  const loadAnalytics = useCallback(
    async (overrides = {}) => {
      try {
        await dispatch(
          getAdminOrderAnalytics({
            days,
            limit,
            ...overrides,
          }),
        );
      } catch (err) {
        toast.error(
          getSafeUserMessage(
            err?.message,
            "We could not load order analytics right now. Please try again later.",
          ),
        );
      } finally {
        setDidLoad(true);
      }
    },
    [days, dispatch, limit],
  );

  useEffect(() => {
    loadAnalytics();

    return () => {
      dispatch(resetAdminOrderError());
      dispatch(resetAdminOrderMeta());
    };
  }, [dispatch, loadAnalytics]);

  const derived = useMemo(() => {
    const orderStatusRows = normalizeBreakdownRows(breakdowns.orderStatus);
    const paymentStatusRows = normalizeBreakdownRows(breakdowns.paymentStatus);
    const providerRows = normalizeBreakdownRows(breakdowns.provider);
    const fulfillmentRows = normalizeBreakdownRows(
      breakdowns.fulfillmentStatus,
    );

    const safeRevenueRows = Array.isArray(revenueByDay)
      ? revenueByDay.map((row) => ({
          date: row.date,
          revenue: Number(row.revenue || 0),
          orders: Number(row.orders || 0),
        }))
      : [];

    const maxRevenue = Math.max(
      ...safeRevenueRows.map((row) => Number(row.revenue || 0)),
      1,
    );

    return {
      orderStatusRows,
      paymentStatusRows,
      providerRows,
      fulfillmentRows,
      revenueRows: safeRevenueRows,
      maxRevenue,
    };
  }, [breakdowns, revenueByDay]);

  const applyFilters = () => {
    setDidLoad(false);
    loadAnalytics({ days, limit });
  };

  return (
    <main className="admin-order-analytics">
      <Meta
        title="Order Analytics | Academy Costumes London"
        description="Admin analytics for Academy Costumes London orders."
        robots="noindex, nofollow"
      />

      <section className="admin-order-analytics__hero">
        <div>
          <Link to="/admin/orders" className="admin-order-analytics__back">
            <ArrowLeft size={16} />
            Back to orders
          </Link>

          <p className="admin-order-analytics__eyebrow">Order intelligence</p>

          <h1>Order Analytics</h1>

          <p className="admin-order-analytics__subtitle">
            Operational snapshot for order volume, payment health, fulfilment
            readiness, provider mix, refunds, and recent revenue.
          </p>
        </div>

        <button
          type="button"
          className="admin-order-analytics__dark-btn"
          onClick={() => loadAnalytics()}
          disabled={refreshing}
        >
          {refreshing ? (
            <Loader2 size={16} className="is-spinning" />
          ) : (
            <RefreshCw size={16} />
          )}
          Refresh
        </button>
      </section>

      <section className="admin-order-analytics__panel">
        <header>
          <h2>Report controls</h2>

          {generatedAt ? (
            <span>Generated {formatDateTime(generatedAt)}</span>
          ) : null}
        </header>

        <div className="admin-order-analytics__control-row">
          <label>
            <span>Range</span>

            <select
              value={days}
              onChange={(event) => setDays(Number(event.target.value || 90))}
              disabled={refreshing}
            >
              {DAY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  Last {option} days
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Recent order limit</span>

            <select
              value={limit}
              onChange={(event) => setLimit(Number(event.target.value || 50))}
              disabled={refreshing}
            >
              {LIMIT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option} orders
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            className="admin-order-analytics__dark-btn"
            onClick={applyFilters}
            disabled={refreshing}
          >
            Apply
          </button>
        </div>

        {range ? (
          <p className="admin-order-analytics__range-note">
            Showing orders from {formatDate(range.from)} to{" "}
            {range.to ? formatDate(range.to) : "now"}.
          </p>
        ) : null}
      </section>

      {error ? <Message variant="danger">{error}</Message> : null}

      {loading ? (
        <div className="admin-order-analytics__loader">
          <Loader />
        </div>
      ) : (
        <>
          <section className="admin-order-analytics__stats">
            <StatCard
              icon={PackageCheck}
              title="Total orders"
              value={Number(summary.totalOrders || 0)}
              subtitle="Within selected range"
            />

            <StatCard
              icon={CheckCircle2}
              title="Paid orders"
              value={Number(summary.paidOrders || 0)}
              subtitle="Paid, partially refunded, refunded"
            />

            <StatCard
              icon={CreditCard}
              title="Pending payments"
              value={Number(summary.pendingPayments || 0)}
              subtitle="Awaiting payment/action"
            />

            <StatCard
              icon={Truck}
              title="Ready to fulfil"
              value={
                Number(summary.readyToShipOrders || 0) +
                Number(summary.readyForPickupOrders || 0)
              }
              subtitle={`${Number(
                summary.readyToShipOrders || 0,
              )} ship · ${Number(summary.readyForPickupOrders || 0)} pickup`}
            />

            <StatCard
              icon={Banknote}
              title="Revenue"
              value={formatMoney(summary.recentRevenue || 0, "GBP")}
              subtitle="Captured paid revenue"
            />

            <StatCard
              icon={BarChart3}
              title="Average order"
              value={formatMoney(summary.recentAverageOrder || 0, "GBP")}
              subtitle="Paid revenue / paid orders"
            />

            <StatCard
              icon={RotateCcw}
              title="Refunded orders"
              value={Number(summary.refundedOrders || 0)}
              subtitle="Partial or full refunds"
            />

            <StatCard
              icon={CreditCard}
              title="Failed payments"
              value={Number(summary.failedPayments || 0)}
              subtitle="Requires attention"
            />
          </section>

          <section className="admin-order-analytics__grid">
            <article className="admin-order-analytics__panel admin-order-analytics__panel--wide">
              <header>
                <h2>Revenue by order date</h2>

                <span>{derived.revenueRows.length} days</span>
              </header>

              {derived.revenueRows.length === 0 ? (
                <div className="admin-order-analytics__empty-inline">
                  No paid revenue in this range yet.
                </div>
              ) : (
                <div className="admin-order-analytics__revenue-bars">
                  {derived.revenueRows.map((row) => {
                    const height = Math.max(
                      10,
                      (Number(row.revenue || 0) / derived.maxRevenue) * 100,
                    );

                    return (
                      <div
                        className="admin-order-analytics__revenue-column"
                        key={row.date}
                      >
                        <div>
                          <span style={{ height: `${height}%` }} />
                        </div>

                        <strong>{formatMoney(row.revenue, "GBP")}</strong>

                        <small>{formatDate(row.date)}</small>

                        <em>
                          {row.orders} {row.orders === 1 ? "order" : "orders"}
                        </em>
                      </div>
                    );
                  })}
                </div>
              )}
            </article>

            <BarList
              title="Payment status mix"
              rows={derived.paymentStatusRows}
              total={Number(summary.totalOrders || 0)}
            />

            <BarList
              title="Order status mix"
              rows={derived.orderStatusRows}
              total={Number(summary.totalOrders || 0)}
            />

            <BarList
              title="Fulfilment status mix"
              rows={derived.fulfillmentRows}
              total={Number(summary.totalOrders || 0)}
            />

            <BarList
              title="Provider mix"
              rows={derived.providerRows}
              total={Number(summary.totalOrders || 0)}
            />

            <article className="admin-order-analytics__panel admin-order-analytics__panel--wide">
              <header>
                <h2>Recent orders</h2>

                <span>{recentOrders.length} loaded</span>
              </header>

              <div className="admin-order-analytics__table-wrap">
                <table className="admin-order-analytics__table">
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Customer</th>
                      <th>Date</th>
                      <th>Payment</th>
                      <th>Status</th>
                      <th>Fulfilment</th>
                      <th>Total</th>
                    </tr>
                  </thead>

                  <tbody>
                    {recentOrders.length === 0 ? (
                      <tr>
                        <td colSpan="7">
                          <div className="admin-order-analytics__empty-inline">
                            No recent orders found.
                          </div>
                        </td>
                      </tr>
                    ) : null}

                    {recentOrders.map((order) => (
                      <tr key={order.id || order._id || order.orderNumber}>
                        <td>
                          <Link
                            to={`/admin/orders/${encodeURIComponent(
                              order.orderNumber,
                            )}`}
                          >
                            {order.orderNumber}
                          </Link>
                        </td>

                        <td>
                          <strong>{order.customer?.name || "Customer"}</strong>
                          <span>{order.customer?.email || "—"}</span>
                        </td>

                        <td>{formatDateTime(getOrderDate(order))}</td>

                        <td>{humanize(getPaymentStatus(order))}</td>

                        <td>{humanize(getOrderStatus(order))}</td>

                        <td>{humanize(getFulfillmentStatus(order))}</td>

                        <td>
                          {formatMoney(orderValue(order), order.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>
          </section>
        </>
      )}
    </main>
  );
}
